import Grid from '@config/Grid/Grid'
import Tile from '@/shared/components/Tile/Tile'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import styles from '@/features/dashboard/Dashboard.module.scss'
import { AreaChart } from '@/features/dashboard/Charts/AreaChart/AreaChart'
import { BarChart } from '@/features/dashboard/Charts/BarChart/BarChart'
import { QuantityTable } from '@/features/dashboard/components/QuantityTable/QuantityTable'
import Button from '@/shared/components/Button/Button'
import {
  TotalSalesCountTotalCount,
  TotalSalesCountTrend
} from '@/features/dashboard/api/DashboardAPI.model'
import DashboardExportModal from '@/shared/components/Modal/DashboardExportModal/DashboardExportModal'
import InsightsAPI from '@/features/dashboard/api/insights/InsightsAPI'
import { QUERY } from '@/features/dashboard/api/insights/InsightsAPI.model'
import { msUntilNext6AM } from '@/shared/utils/getMsUntilNext6AM'
import {
  currencySymbol,
  formatPrice,
  minorToMajor
} from '@/shared/utils/price'
import Skeleton from '@/shared/components/Skeleton/Skeleton'

const totalSalesValueGeneralLoadingPlaceholder = (
  <div className="flex absolute flex-col items-start h-full w-full items-center p-5 pr-6 pl-6 pb-3">
    <div className="flex flex-col w-full h-auto gap-2 pt-[2.125rem]">
      <Skeleton variant="rounded" width="50%" height="2rem" />
      <Skeleton variant="rounded" width="25%" height="1rem" />
    </div>
    <div className="flex w-full h-full pt-6 pb-3">
      <Skeleton variant="rounded" width="100%" height="100%" />
    </div>
  </div>
)

const totalSalesValueAverageGeneralLoadingPlaceholder = (
  <div className="flex absolute  flex-col items-start gap-2 h-full w-full items-center p-5 pr-6 pl-6 pb-3">
    <div className="flex w-full h-auto justify-between pt-[2.625rem] pr-[10rem]">
      <div className="flex flex-col gap-1 w-[20%]">
        <Skeleton variant="rounded" width="100%" height="1.25rem" />
        <Skeleton variant="rounded" width="85%" height="1.75rem" />
      </div>
      <div className="flex flex-col gap-1 w-[20%]">
        <Skeleton variant="rounded" width="100%" height="1.25rem" />
        <Skeleton variant="rounded" width="85%" height="1.75rem" />
      </div>
      <div className="flex flex-col gap-1 w-[20%]">
        <Skeleton variant="rounded" width="100%" height="1.25rem" />
        <Skeleton variant="rounded" width="85%" height="1.75rem" />
      </div>
      <div className="flex flex-col gap-1 w-[20%]">
        <Skeleton variant="rounded" width="100%" height="1.25rem" />
        <Skeleton variant="rounded" width="85%" height="1.5rem" />
      </div>
    </div>
    <div className="flex w-full h-full pt-3 pb-3">
      <Skeleton variant="rounded" width="100%" height="100%" />
    </div>
  </div>
)

const totalSalesCountGeneralLoadingPlaceholder = (
  <div className="flex absolute flex-col items-start gap-2 h-full w-full items-center justify-center p-5 pr-6 pl-6 pb-3">
    <div className="flex w-full h-[100%] mt-[3.5rem] pb-3">
      <Skeleton variant="rounded" width="100%" height="100%" />
    </div>
  </div>
)

function Dashboard() {
  const endDate = '2025-12-01'

  const defaultStartDate = useMemo(() => {
    const [y, m, d] = endDate.split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d))
    dt.setUTCDate(dt.getUTCDate() - 90)
    return dt.toISOString().slice(0, 10)
  }, [endDate])

  const [
    totalSalesValueAverageGeneralStartDate,
    setTotalSalesValueAverageGeneralStartDate
  ] = useState(defaultStartDate)

  const totalSalesValueAverageGeneralStartDateMemo = useMemo(
    () => totalSalesValueAverageGeneralStartDate.slice(0, 10),
    [totalSalesValueAverageGeneralStartDate]
  )

  const {
    data: totalSalesValueAverageGeneral,
    isLoading: totalSalesValueAverageGeneralLoading
  } = useQuery({
    queryKey: [
      QUERY.TOTAL_SALES_VALUE_AVERAGE_GENERAL,
      totalSalesValueAverageGeneralStartDateMemo
    ],
    queryFn: () =>
      InsightsAPI.getTotalSalesAveragesGeneral({
        start_date: totalSalesValueAverageGeneralStartDateMemo,
        end_date: '2025-12-01'
      }),
    staleTime: msUntilNext6AM(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const [totalSalesCountGeneralStartDate, setTotalSalesCountGeneralStartDate] =
    useState(defaultStartDate)

  const {
    data: totalSalesCountGeneral,
    isLoading: totalSalesCountGeneralLoading
  } = useQuery({
    queryKey: [
      QUERY.TOTAL_SALES_COUNT_GENERAL,
      totalSalesCountGeneralStartDate
    ],
    queryFn: () =>
      InsightsAPI.getTotalSalesCountGeneral({
        start_date: totalSalesCountGeneralStartDate.slice(0, 10),
        end_date: '2025-12-01'
      }),
    staleTime: msUntilNext6AM(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const [totalSalesValueGeneralStartDate, setTotalSalesValueGeneralStartDate] =
    useState(defaultStartDate)

  const {
    data: totalSalesValueGeneral,
    isLoading: totalSalesValueGeneralLoading
  } = useQuery({
    queryKey: [
      QUERY.TOTAL_SALES_VALUE_GENERAL,
      totalSalesValueGeneralStartDate
    ],
    queryFn: () =>
      InsightsAPI.getTotalSalesValueGeneral({
        start_date: totalSalesValueGeneralStartDate.slice(0, 10),
        end_date: '2025-12-01'
      }),
    staleTime: msUntilNext6AM(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const { t } = useTranslation()

  const totalSalesDescription = (
    <div className={styles.totalSalesTopDescriptionContainer}>
      <div className={styles.totalSalesTopDescription}>
        <span className="headline-bold-large">
          {formatPrice(totalSalesValueGeneral?.totalSales, {
            withCurrency: false
          }) || 0}
        </span>
        <span className="title-large">{currencySymbol()}</span>
      </div>
      <span>{t('DASHBOARD.SALES_OVER_TIME')}</span>
    </div>
  )

  const averageOrderValueDescription = (
    <div className={styles.averageOrderValueDescriptionContainer}>
      <div className={styles.averageOrderValueDescription}>
        <span className="title-small">
          {t('DASHBOARD.AVERAGE_ORDER_VALUE')}
        </span>
        <div className={styles.averageOrderValueDescriptionValue}>
          <span className="title-bold-large">
            {formatPrice(totalSalesValueAverageGeneral?.averageOrderValuePeriod, {
              withCurrency: false
            }) || 0}
          </span>
          <span className="title-medium">{currencySymbol()}</span>
        </div>
      </div>
      <div className={styles.averageOrderValueDescription}>
        <span className="title-small">
          {t('DASHBOARD.HIGHEST_ORDER_VALUE')}
        </span>
        <div className={styles.averageOrderValueDescriptionValue}>
          <span className="title-bold-large">
            {formatPrice(totalSalesValueAverageGeneral?.highestOrderValuePeriod, {
              withCurrency: false
            }) || 0}
          </span>
          <span className="title-medium">{currencySymbol()}</span>
        </div>
      </div>
      <div className={styles.averageOrderValueDescription}>
        <span className="title-small">{t('DASHBOARD.TOTAL_ORDERS')}</span>
        <span className="title-bold-large">
          {totalSalesValueAverageGeneral?.totalOrders || 0}
        </span>
      </div>
      <div className={styles.averageOrderValueDescription}>
        <span className="title-small">{t('DASHBOARD.TOTAL_SALES')}</span>
        <div className={styles.averageOrderValueDescriptionValue}>
          <span className="title-bold-large">
            {formatPrice(totalSalesValueAverageGeneral?.totalSales, {
              withCurrency: false
            }) || 0}{' '}
          </span>
          <span className="title-medium">{currencySymbol()}</span>
        </div>
      </div>
    </div>
  )

  const mappedTotalSalesCountTrend = totalSalesCountGeneral?.dailyCounts?.map(
    (item: TotalSalesCountTrend) => ({
      date: item.date,
      trend: item.trend
    })
  )

  const mappedTotalSalesTotalCount = totalSalesCountGeneral?.dailyCounts?.map(
    (item: TotalSalesCountTotalCount) => ({
      date: item.date,
      totalCount: item.totalCount
    })
  )

  // Charts plot major units, the API delivers every amount in minor ones.
  const areaChartData = [
    {
      dataKey: 'totalSales',
      chartData:
        (totalSalesValueGeneral?.dailySales || []).map((item: any) => ({
          date: item.date,
          totalSales: minorToMajor(item.totalSales)
        })) || []
    },
    {
      dataKey: 'profit',
      chartData:
        (totalSalesValueGeneral?.dailySales || []).map((item: any) => ({
          date: item.date,
          profit: minorToMajor(item.profit)
        })) || []
    },
    {
      dataKey: 'cost',
      chartData:
        (totalSalesValueGeneral?.dailySales || []).map((item: any) => ({
          date: item.date,
          cost: minorToMajor(item.cost)
        })) || []
    }
  ]

  const averageOrderValueChartData = (
    totalSalesValueAverageGeneral?.dailiesAverages || []
  ).map(item => ({
    ...item,
    averageOrderValue: minorToMajor(item.averageOrderValue)
  }))

  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  return (
    <Grid>
      <div className={`${styles.dashboardWrapper} start-2 col-11`}>
        <Tile color="black">
          <div className={styles.dashboardContainer}>
            <div id="tour-dashboard-content" className={styles.dashboardHeader}>
              <p className={`title-bold-medium ${styles.dashboardTitle}`}>
                {t('DASHBOARD.TITLE')}
              </p>
              <div
                id="tour-dashboard-export"
                className={styles.dashboardHeaderToolbar}
              >
                <Button
                  label={t('DASHBOARD.EXPORT_BUTTON')}
                  variant="text"
                  icon="las la-download"
                  color="primary"
                  onClick={() => setIsExportModalOpen(!isExportModalOpen)}
                />
              </div>
            </div>
            <div className={styles.dashboardContent}>
              <div id="tour-dashboard-chart" className={styles.dashboardContentContainer}>
                <AreaChart
                  title={t('DASHBOARD.TOTAL_SALES')}
                  description={totalSalesDescription}
                  chartsData={areaChartData}
                  flex={1}
                  setStartDate={setTotalSalesValueGeneralStartDate}
                  isLoading={totalSalesValueGeneralLoading}
                  loadingPlaceholder={totalSalesValueGeneralLoadingPlaceholder}
                />
                <BarChart
                  title={t('DASHBOARD.AVERAGE_ORDER_VALUE')}
                  description={averageOrderValueDescription}
                  chartData={averageOrderValueChartData}
                  dataKey="averageOrderValue"
                  flex={2}
                  hideGap
                  setStartDate={setTotalSalesValueAverageGeneralStartDate}
                  isLoading={totalSalesValueAverageGeneralLoading}
                  loadingPlaceholder={
                    totalSalesValueAverageGeneralLoadingPlaceholder
                  }
                />
              </div>
              <div className={styles.dashboardContentContainer}>
                <BarChart
                  title={t('DASHBOARD.ORDER_COUNTS')}
                  chartData={mappedTotalSalesTotalCount || []}
                  dataKey="totalCount"
                  lineChartData={mappedTotalSalesCountTrend || []}
                  lineChartDataKey="trend"
                  hideGap
                  flex={1}
                  setStartDate={setTotalSalesCountGeneralStartDate}
                  isLoading={totalSalesCountGeneralLoading}
                  loadingPlaceholder={totalSalesCountGeneralLoadingPlaceholder}
                />
                <QuantityTable title={t('DASHBOARD.UNITS_SOLD')} flex={1} />
              </div>
            </div>
          </div>
        </Tile>
        <DashboardExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
        />
      </div>
    </Grid>
  )
}

export default Dashboard
